/**
 * Hace que el botón "atrás" (navegador o gesto/botón físico en mobile) cierre el modal
 * abierto en lugar de navegar a la página anterior.
 *
 * Técnica: al abrir un modal se agrega una entrada de historial "fantasma" (mismo path,
 * sin cambiar la ruta). Si el usuario presiona "atrás", el navegador consume esa entrada,
 * dispara `popstate` y en vez de dejar navegar, cerramos el modal que está en la punta
 * de la pila. Si el modal se cierra por otro medio (botón X, click afuera, guardar, etc.),
 * limpiamos esa misma entrada fantasma con `history.back()` para no dejar "basura" en el
 * historial (evitando así que haga falta un segundo "atrás" para salir de la página).
 */

const stack = [];
let skipNextPopstate = false;

/**
 * Red de seguridad: si un modal de Bootstrap se cierra en medio de su transición
 * (por ejemplo porque el componente que lo contiene se vuelve a renderizar o se
 * destruye mientras el botón "atrás" lo está cerrando) puede quedar un
 * `.modal-backdrop` "huérfano" tapando toda la pantalla sin que se pueda
 * interactuar con nada. Si ya no hay ningún modal visible, limpiamos cualquier
 * backdrop/estado residual.
 */
export function limpiarBackdropsHuerfanos() {
    if (typeof document === 'undefined') return;
    if (document.querySelector('.modal.show')) return;
    document.querySelectorAll('.modal-backdrop').forEach((el) => el.remove());
    document.body.classList.remove('modal-open');
    document.body.style.removeProperty('padding-right');
    document.body.style.removeProperty('overflow');
}

function handlePopState() {
    if (skipNextPopstate) {
        skipNextPopstate = false;
        return;
    }
    const top = stack.pop();
    if (!top) return;
    try {
        top.close();
    } catch (_) {
        /* el modal ya pudo haberse cerrado/destruido; no hacemos nada más */
    } finally {
        window.setTimeout(limpiarBackdropsHuerfanos, 400);
    }
}

if (typeof window !== 'undefined') {
    window.addEventListener('popstate', handlePopState);
}

let uid = 0;

/**
 * Registra un modal como "abierto": agrega la entrada fantasa al historial.
 * @param {() => void} close Función que cierra el modal (llamada si el usuario presiona "atrás").
 * @returns {number} id a pasar luego a popModalGuard cuando el modal se cierre.
 */
export function pushModalGuard(close) {
    const id = ++uid;
    stack.push({ id, close });
    try {
        window.history.pushState({ __modalGuard: id }, document.title, window.location.href);
    } catch (_) {
        /* entornos sin History API (SSR, tests, etc.) */
    }
    return id;
}

/**
 * Saca el guard de la pila sin tocar el historial (p. ej. antes de navegar a otra ruta).
 */
export function dismissModalGuard(id) {
    if (id == null) return;
    const idx = stack.findIndex((entry) => entry.id === id);
    if (idx !== -1) stack.splice(idx, 1);
}

/**
 * Reemplaza la entrada fantasma del historial por una normal, sin disparar popstate.
 * Usar al navegar desde un overlay en lugar de history.back().
 */
export function clearModalGuardHistoryEntry() {
    try {
        if (window.history.state && window.history.state.__modalGuard != null) {
            window.history.replaceState(null, document.title, window.location.href);
        }
    } catch (_) {
        /* ignore */
    }
}

/**
 * Avisa que el modal se cerró (por cualquier medio distinto de "atrás").
 * Limpia la pila y, si corresponde, la entrada fantasma del historial.
 */
export function popModalGuard(id) {
    if (id == null) return;
    const idx = stack.findIndex((entry) => entry.id === id);
    if (idx !== -1) stack.splice(idx, 1);

    try {
        if (window.history.state && window.history.state.__modalGuard === id) {
            skipNextPopstate = true;
            window.history.back();
        }
    } catch (_) {
        /* ignore */
    }
}
